import {
  HeaderSizedCell,
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import { FC, RefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledActionSizedCell } from '../styles';
import { Access, IntegrationSecret, MenuAction } from './types';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import { colorsButton } from '__app/colors';
import Icon from '__components/Kizen/Icon';
import { TextCell } from './styles';

type UseColumnsProps = {
  onSelectAction: (action: MenuAction, secret: IntegrationSecret) => void;
  access: Access;
  tableWrapperRef: RefObject<HTMLDivElement>;
};

type ActionsCellProps = UseColumnsProps & {
  data?: IntegrationSecret;
};

const COLUMN_WIDTH = {
  value: '135px',
  description: '135px',
  employee: '220px',
  date: '170px',
  actions: '51px',
  padding: '20px',
};

const OPTION_HEIGHT = 32;

const ActionCell: FC<ActionsCellProps> = ({
  access,
  data,
  onSelectAction,
  tableWrapperRef,
}) => {
  const { t } = useTranslation();
  const [above, setAbove] = useState(false);

  const options = useMemo(
    () =>
      [
        { value: 'view', label: t('View Details') },
        access.edit && { value: 'edit', label: t('Edit') },
        access.remove && { value: 'delete', label: t('Delete') },
      ].filter(Boolean),
    [access, t]
  );

  const handleChange = ({ value }: { value: MenuAction }) => {
    onSelectAction(value, data!);
  };

  const handleOpen = (
    isOpen: boolean,
    targetRef: RefObject<HTMLButtonElement>
  ) => {
    if (tableWrapperRef.current && targetRef.current) {
      const { bottom } = tableWrapperRef.current.getBoundingClientRect();
      const { bottom: targetBottom } =
        targetRef.current.getBoundingClientRect();
      setAbove(bottom < targetBottom + options.length * OPTION_HEIGHT);
    }
  };

  return options.length ? (
    <StyledActionSizedCell
      minWidth={COLUMN_WIDTH.actions}
      padding="0px"
      data-qa="cell-actions"
    >
      <IconButtonAbsoluteMenu
        title={
          access.edit
            ? t('Edit Integration Secret')
            : t('View Integration Secret')
        }
        sizing="dense"
        position="right"
        color={colorsButton.iconGray}
        onChange={handleChange}
        onOpen={handleOpen}
        options={options}
        above={above}
      >
        <Icon icon="three-dot" />
      </IconButtonAbsoluteMenu>
    </StyledActionSizedCell>
  ) : null;
};

export const useColumns = ({
  onSelectAction,
  access,
  tableWrapperRef,
}: UseColumnsProps) => {
  const { t } = useTranslation();

  return useMemo(() => {
    return [
      {
        id: 'spacer',
        headCell: <SizedCell width="20px" />,
        cell: <SizedCell width="20px" />,
      },
      {
        id: 'obfuscated_value',
        headCell: (
          <HeaderSizedCell
            label={t('Secret')}
            minWidth={COLUMN_WIDTH.value}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.value}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-obfuscated_value"
          />
        ),
        format: (obfuscated_value: string) => {
          return (
            <TextCell>
              <TextEllipsisWithTooltip size="micro">
                {obfuscated_value}
              </TextEllipsisWithTooltip>
            </TextCell>
          );
        },
      },
      {
        id: 'description',
        headCell: (
          <SortableHeadCell
            by="description"
            label={t('Description')}
            minWidth={COLUMN_WIDTH.description}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.description}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-description"
          />
        ),
        format: (description: string) => {
          return (
            <TextCell>
              <TextEllipsisWithTooltip size="micro">
                {description}
              </TextEllipsisWithTooltip>
            </TextCell>
          );
        },
      },
      {
        id: 'updated',
        headCell: (
          <SortableHeadCell
            label={t('Date Updated')}
            numeric
            by="updated"
            minWidth={COLUMN_WIDTH.date}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.date}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-updated"
          />
        ),
        format: (date: string) => {
          return <DateTimeInputInline value={date} readOnly showDateTooltip />;
        },
      },
      {
        id: 'updated_by',
        headCell: (
          <SortableHeadCell
            label={t('Updated By')}
            by="updated_by"
            minWidth={COLUMN_WIDTH.employee}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.employee}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-updated_by"
          />
        ),
        format: (teamMember: IntegrationSecret['updated_by']) => {
          return (
            <TextCell>
              <TextEllipsisWithTooltip size="micro">
                {teamMember?.display_name}
              </TextEllipsisWithTooltip>
            </TextCell>
          );
        },
      },
      {
        id: 'business_plugin_app',
        headCell: (
          <SortableHeadCell
            by="business_plugin_app"
            label={t('Plugin App')}
            minWidth={COLUMN_WIDTH.description}
            padding={COLUMN_WIDTH.padding}
          />
        ),
        cell: (
          <SizedCell
            minWidth={COLUMN_WIDTH.description}
            padding={COLUMN_WIDTH.padding}
            data-qa="cell-business_plugin_app"
          />
        ),
        format: (plugin: IntegrationSecret['business_plugin_app']) => {
          return (
            <TextCell>
              <TextEllipsisWithTooltip size="micro">
                {plugin?.plugin_app_name}
              </TextEllipsisWithTooltip>
            </TextCell>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <HeaderSizedCell
            label={t('Actions')}
            minWidth={COLUMN_WIDTH.actions}
            padding="0px"
            align="right"
            t={t}
          />
        ),
        cell: (
          <ActionCell
            onSelectAction={onSelectAction}
            access={access}
            tableWrapperRef={tableWrapperRef}
          />
        ),
      },
      {
        id: 'right-spacer',
        headCell: <SizedCell width="20px" />,
        cell: <SizedCell width="20px" />,
      },
    ];
  }, [access, onSelectAction, t, tableWrapperRef]);
};
